import axios from 'axios';

import {
  startLoading,
  stopLoading,
  networkErrorLoading,
  requestSucceeded,
} from '../components/GlobalLoader/loaderService';

// Axios instance
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_URL_BACKEND_SERVER,
  withCredentials: true,
  timeout: 30000,
});

let isRefreshing = false;
let failedQueue = [];

// Проверяем, нужно ли учитывать запрос в глобальном loader
const shouldTrackLoader = (config) => {
  return config?.skipGlobalLoader !== true;
};

// Завершаем учёт запроса с учётом типа ошибки
const finishRequestWithError = (config, error) => {
  if (!shouldTrackLoader(config)) {
    return;
  }

  if (!error?.response && !axios.isCancel(error)) {
    networkErrorLoading();
  } else {
    stopLoading();
  }
};

// Обработка очереди запросов, ожидающих обновления токена
const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    if (shouldTrackLoader(config)) {
      startLoading();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  // Успешный ответ
  (response) => {
    if (shouldTrackLoader(response.config)) {
      requestSucceeded();
    }

    return response;
  },

  // Ошибки и обновление токена
  async (error) => {
    const originalRequest = error.config;

    // Сетевая ошибка: сервер недоступен, timeout и т. п.
    if (!error.response && !axios.isCancel(error)) {
      finishRequestWithError(originalRequest, error);

      return Promise.reject(error);
    }

    // Обработка 401 и автоматическое обновление токена
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      // Не обновляем токен для login и refresh
      if (
        originalRequest.url?.includes('auth2/login') ||
        originalRequest.url?.includes('auth2/refresh')
      ) {
        finishRequestWithError(originalRequest, error);

        return Promise.reject(error);
      }

      // Помечаем запрос, чтобы избежать повторного refresh
      originalRequest._retry = true;

      // Refresh уже выполняется — ждём его завершения
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(
          (token) => {
            // Закрываем учёт исходной попытки
            if (shouldTrackLoader(originalRequest)) {
              stopLoading();
            }

            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;

            // Повторный запрос будет учтён request interceptor
            return apiClient(originalRequest);
          },
          (refreshError) => {
            // Обрабатываем только ошибку очереди refresh.
            // Ошибка повторного запроса сюда не попадёт.
            finishRequestWithError(originalRequest, refreshError);

            return Promise.reject(refreshError);
          }
        );
      }

      isRefreshing = true;

      try {
        // Refresh выполняется обычным axios, вне глобального loader
        const response = await axios.post(
          `${process.env.REACT_APP_URL_BACKEND_SERVER}/auth2/refresh`,
          {},
          {
            withCredentials: true,
            timeout: 30000,
          }
        );

        const newAccessToken = response.data?.accessToken;

        if (!newAccessToken) {
          throw new Error('Refresh failed: accessToken is missing');
        }

        localStorage.setItem('access_token', newAccessToken);

        apiClient.defaults.headers.common.Authorization =
          `Bearer ${newAccessToken}`;

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        // Разрешаем запросы, ожидающие refresh
        processQueue(null, newAccessToken);

        // Завершаем учёт исходной попытки
        if (shouldTrackLoader(originalRequest)) {
          stopLoading();
        }

        // Новая попытка будет учтена request interceptor
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');

        finishRequestWithError(originalRequest, refreshError);

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Обычная HTTP-ошибка или повторный 401
    finishRequestWithError(originalRequest, error);

    return Promise.reject(error);
  }
);

// Универсальная функция запросов
export const request = async (
  endpoint,
  method,
  data = null,
  params = null,
  header = {},
  options = {}
) => {
  const token = localStorage.getItem('access_token');

  const headers = { ...header };

  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await apiClient({
    url: `/${endpoint}`,
    method,
    data,
    params,
    headers,

    // Отключаем глобальный loader только для выбранных запросов
    skipGlobalLoader: options.skipGlobalLoader === true,
  });

  return response.data;
};